import React, {useState} from 'react';
import {ExportButton, List, useListContext, usePermissions, useRedirect, useTranslate} from 'react-admin';
import {Box, Typography} from '@mui/material';
import {AddRounded} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import StatamicListPage from '../components/statamic/StatamicListPage';
import PrimaryButton from '../components/statamic/PrimaryButton';
import {FilterDropdown} from '../components/statamic/FilterBar';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import MonoChip from '../components/statamic/MonoChip';
import {useUmbrellaChoices} from '../lib/customHooks';

const TextCell = ({value}) => (
    <Typography
        sx={{
            fontSize: `${t.type.body.size}px`,
            color: value ? t.color.text.secondary : t.color.text.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        }}
    >
        {value ?? '—'}
    </Typography>
);

const FieldNameCell = ({record}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
        <StatusDot variant={record.is_active ? 'success' : 'neutral'}/>
        <Typography
            sx={{
                fontSize: `${t.type.bodyStrong.size}px`,
                fontWeight: t.type.bodyStrong.weight,
                color: t.color.text.primary,
                fontFamily: t.font.mono,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {record.field_name || '—'}
        </Typography>
    </Box>
);

const PermissionsCell = ({record, translate}) => {
    const chips = [];
    if (record.charity_can_edit) {
        chips.push(<StatusPill key="edit" variant="success">{translate('Edit')}</StatusPill>);
    } else if (record.charity_can_view) {
        chips.push(<StatusPill key="view" variant="info">{translate('View')}</StatusPill>);
    } else {
        chips.push(<StatusPill key="none" variant="neutral">None</StatusPill>);
    }
    return <Box sx={{display: 'flex', gap: 0.5}}>{chips}</Box>;
};

const MetadataDefinitionListStatamicInner = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const [activeViewId, setActiveViewId] = useState('active');

    const isAdmin = !!permissions && permissions.includes?.('admin');
    const canEdit = !!permissions && (permissions.includes?.('admin') || permissions.includes?.('umbrella'));
    const canCreate = canEdit;

    const umbrellaChoices = useUmbrellaChoices();
    const getUmbrellaName = (id) => {
        if (!umbrellaChoices || !id) return null;
        const u = umbrellaChoices.find(c => c.id === id);
        return u ? u.name : `ID: ${id}`;
    };

    const views = [
        {id: 'all',      label: translate('All')},
        {id: 'active',   label: translate('Active')},
        {id: 'inactive', label: translate('Inactive')},
    ];

    const handleViewSelect = (id) => {
        setActiveViewId(id);
        const base = {...filterValues};
        delete base.is_active;
        if (id === 'active')        setFilters({...base, is_active: true}, displayedFilters);
        else if (id === 'inactive') setFilters({...base, is_active: false}, displayedFilters);
        else                        setFilters(base, displayedFilters);
    };

    const columns = [
        {
            key: 'field_name', label: translate('Field Name'), sortable: true, sortField: 'field_name', width: 200,
            render: (r) => <FieldNameCell record={r}/>,
        },
        {
            key: 'label', label: translate('Label'),
            render: (r) => <TextCell value={r.field_label}/>,
        },
        {
            key: 'entity', label: translate('Entity Type'), width: 120,
            render: (r) => r.entity_type ? <MonoChip>{r.entity_type}</MonoChip> : <TextCell value={null}/>,
        },
        {
            key: 'type', label: translate('Field Type'), width: 110,
            render: (r) => r.field_type ? <MonoChip>{r.field_type}</MonoChip> : <TextCell value={null}/>,
        },
        {
            key: 'required', label: translate('Required'), width: 90,
            render: (r) => (
                <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                    <StatusDot variant={r.is_required ? 'warning' : 'neutral'}/>
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                        {r.is_required ? 'Yes' : 'No'}
                    </Typography>
                </Box>
            ),
        },
        {
            key: 'perms', label: translate('Permissions'), width: 110,
            render: (r) => <PermissionsCell record={r} translate={translate}/>,
        },
        {
            key: 'order', label: translate('Order'), width: 80,
            render: (r) => (
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, fontVariantNumeric: 'tabular-nums', textAlign: 'right'}}>
                    {r.display_order ?? '—'}
                </Typography>
            ),
        },
        ...(isAdmin ? [{
            key: 'umbrella', label: translate('Umbrella'), width: 200,
            render: (r) => <TextCell value={getUmbrellaName(r.parent_charity_id)}/>,
        }] : []),
    ];

    const updateFilter = (src, v) => setFilters({...filterValues, [src]: v}, displayedFilters);
    const clearFilter = (src) => {
        const next = {...filterValues};
        delete next[src];
        setFilters(next, displayedFilters);
    };

    const rowActions = (record) => [
        {label: translate('View'), onClick: () => redirect('show', 'metadata-definitions', record.id)},
        ...(canEdit ? [{label: translate('Edit'), onClick: () => redirect('edit', 'metadata-definitions', record.id)}] : []),
    ];

    return (
        <StatamicListPage
            scoped="umbrellaOnly"
            title={translate('Metadata Definitions')}
            subtitle="Custom fields attached to Child, Guardian, and Charity records"
            headerActions={
                <>
                    <ExportButton label={translate('Export')}/>
                    {canCreate && (
                        <PrimaryButton
                            variant="primary"
                            startIcon={<AddRounded sx={{fontSize: 18}}/>}
                            onClick={() => redirect('create', 'metadata-definitions')}
                        >
                            {translate('Create definition')}
                        </PrimaryButton>
                    )}
                </>
            }
            views={views}
            activeView={activeViewId}
            onViewSelect={handleViewSelect}
            searchPlaceholder={translate('Search definitions')}
            filters={
                <FilterDropdown
                    label={translate('Entity Type')}
                    source="entity_type"
                    options={[
                        {id: 'CHILD',    name: translate('Child')},
                        {id: 'GUARDIAN', name: translate('Guardian')},
                        {id: 'CHARITY',  name: translate('Charity')},
                    ]}
                    value={filterValues?.entity_type}
                    onChange={updateFilter}
                    onClear={clearFilter}
                />
            }
            columns={columns}
            rowActions={rowActions}
            onRowClick={(record) => redirect('show', 'metadata-definitions', record.id)}
        />
    );
};

const MetadataDefinitionListStatamic = (props) => (
    <List
        {...props}
        sort={{field: 'display_order', order: 'ASC'}}
        perPage={25}
        filterDefaultValues={{is_active: true}}
        actions={false}
        pagination={false}
        component={Box}
        sx={{
            backgroundColor: t.color.page,
            '& .RaList-main': {backgroundColor: t.color.page},
        }}
    >
        <MetadataDefinitionListStatamicInner/>
    </List>
);

export default MetadataDefinitionListStatamic;
